<template>
  <div class="admin-family-management">
    <div class="page-header">
      <div class="header-left">
        <h1>家庭管理</h1>
        <p>管理系统中的所有家庭信息</p>
      </div>
      <div class="header-right">
        <el-button type="primary" @click="refreshData" :loading="loading">
          <i class="el-icon-refresh"></i> 刷新
        </el-button>
      </div>
    </div>

    <!-- 统计概览 -->
    <div class="overview-cards">
      <el-row :gutter="20">
        <el-col :span="6">
          <div class="overview-card">
            <div class="card-icon families">
              <i class="el-icon-house"></i>
            </div>
            <div class="card-content">
              <div class="card-number">{{ statistics.totalFamilies || 0 }}</div>
              <div class="card-label">总家庭数</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="overview-card">
            <div class="card-icon members">
              <i class="el-icon-user"></i>
            </div>
            <div class="card-content">
              <div class="card-number">{{ statistics.totalMembers || 0 }}</div>
              <div class="card-label">总成员数</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="overview-card">
            <div class="card-icon devices">
              <i class="el-icon-mobile"></i>
            </div>
            <div class="card-content">
              <div class="card-number">{{ statistics.totalDevices || 0 }}</div>
              <div class="card-label">总设备数</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="overview-card">
            <div class="card-icon avg">
              <i class="el-icon-data-analysis"></i>
            </div>
            <div class="card-content">
              <div class="card-number">{{ (statistics.avgMembersPerFamily || 0).toFixed(1) }}</div>
              <div class="card-label">平均成员数</div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 家庭列表 -->
    <div class="family-list-section">
      <div class="section-header">
        <h2>家庭列表</h2>
        <div class="search-bar">
          <el-input
            v-model="searchQuery"
            placeholder="搜索家庭名称..."
            prefix-icon="el-icon-search"
            clearable
            @input="handleSearch"
            style="width: 300px;"
          />
        </div>
      </div>

      <div class="family-grid" v-loading="loading">
        <div 
          v-for="family in paginatedFamilies" 
          :key="family.id"
          class="family-card"
          @click="viewFamilyDetail(family.id)"
        >
          <div class="family-header">
            <div class="family-avatar">
              <i class="el-icon-house"></i>
            </div>
            <div class="family-info">
              <h3 class="family-name">{{ family.name }}</h3>
              <p class="family-code" v-if="family.description">{{ family.description }}</p>
              <p class="family-code" v-else>暂无描述</p>
            </div>
            <div class="family-status">
              <el-tag :type="family.isActive !== false ? 'success' : 'danger'" size="small">
                {{ family.isActive !== false ? '活跃' : '禁用' }}
              </el-tag>
            </div>
          </div>

          <div class="family-stats">
            <div class="stat-item">
              <span class="stat-number">{{ family.memberCount || 0 }}</span>
              <span class="stat-label">成员</span>
            </div>
            <div class="stat-item">
              <span class="stat-number">{{ family.deviceCount || 0 }}</span>
              <span class="stat-label">设备</span>
            </div>
          </div>

          <div class="family-meta">
            <div class="meta-item">
              <i class="el-icon-time"></i>
              <span>创建时间: {{ formatDate(family.createTime) }}</span>
            </div>
            <div class="meta-item" v-if="family.address">
              <i class="el-icon-location"></i>
              <span>{{ family.address }}</span>
            </div>
          </div>

          <div class="family-actions">
            <el-button type="primary" size="small" @click.stop="viewFamilyDetail(family.id)">
              查看详情
            </el-button>
            <el-dropdown @command="handleFamilyAction" trigger="click" @click.stop>
              <el-button size="small">
                更多 <i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item :command="{action: 'edit', family}">编辑信息</el-dropdown-item>
                <el-dropdown-item :command="{action: family.isActive !== false ? 'disable' : 'enable', family}">
                  {{ family.isActive !== false ? '禁用' : '启用' }}家庭
                </el-dropdown-item>
                <el-dropdown-item :command="{action: 'delete', family}" divided>删除家庭</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="filteredFamilies.length === 0 && !loading" class="empty-state">
        <i class="el-icon-house"></i>
        <p>{{ searchQuery ? '未找到匹配的家庭' : '暂无家庭数据' }}</p>
      </div>
      
      <!-- 分页组件 -->
      <div v-if="filteredFamilies.length > 0" class="pagination-wrapper">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[6, 12, 24, 48]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="filteredFamilies.length"
        >
        </el-pagination>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AdminFamilyManagement',
  data() {
    return {
      loading: false,
      searchQuery: '',
      families: [],
      statistics: {},
      pagination: {
        currentPage: 1,
        pageSize: 12
      }
    }
  },
  computed: {
    filteredFamilies() {
      if (!this.searchQuery) {
        return this.families
      }
      return this.families.filter(family => 
        (family.name && family.name.toLowerCase().includes(this.searchQuery.toLowerCase())) ||
        (family.familyName && family.familyName.toLowerCase().includes(this.searchQuery.toLowerCase())) ||
        (family.id && family.id.toLowerCase().includes(this.searchQuery.toLowerCase())) ||
        (family.description && family.description.toLowerCase().includes(this.searchQuery.toLowerCase()))
      )
    },
    paginatedFamilies() {
      const start = (this.pagination.currentPage - 1) * this.pagination.pageSize
      const end = start + this.pagination.pageSize
      return this.filteredFamilies.slice(start, end)
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      try {
        this.loading = true
        await Promise.all([
          this.loadFamilies(),
          this.loadStatistics()
        ])
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },

    async loadFamilies() {
      try {
        const response = await this.$http.get('/admin/api/families/list')
        if (response.data.success) {
          this.families = response.data.data
        }
      } catch (error) {
        console.error('加载家庭列表失败:', error)
        throw error
      }
    },

    async loadStatistics() {
      try {
        const response = await this.$http.get('/admin/api/families/statistics')
        if (response.data.success) {
          this.statistics = response.data.data
        }
      } catch (error) {
        console.error('加载统计数据失败:', error)
        throw error
      }
    },

    refreshData() {
      this.loadData()
    },

    handleSearch() {
      // 搜索逻辑在computed中处理
      // 搜索时重置到第一页
      this.pagination.currentPage = 1
    },

    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.currentPage = 1
    },

    handleCurrentChange(val) {
      this.pagination.currentPage = val
    },

    viewFamilyDetail(familyId) {
      this.$router.push(`/admin/families/${familyId}`)
    },

    handleFamilyAction(command) {
      const { action, family } = command
      
      switch (action) {
        case 'edit':
          this.editFamily(family)
          break
        case 'disable':
          this.disableFamily(family)
          break
        case 'enable':
          this.enableFamily(family)
          break
        case 'delete':
          this.deleteFamily(family)
          break
      }
    },

    editFamily(family) {
      // 跳转到家庭编辑页面或显示编辑对话框
      this.$router.push(`/admin/families/${family.id}/edit`)
    },

    async disableFamily(family) {
      try {
        const result = await this.$confirm(
          `确定要禁用家庭"${family.name || family.familyName}"吗？`,
          '确认禁用',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        const response = await this.$http.put(`/admin/api/families/${family.id}/status`, {
          isActive: false
        })

        if (response.data.success) {
          this.$message.success('家庭已禁用')
          this.loadFamilies()
        } else {
          this.$message.error(response.data.message || '禁用失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('禁用家庭失败:', error)
          this.$message.error('禁用失败')
        }
      }
    },

    async enableFamily(family) {
      try {
        const response = await this.$http.put(`/admin/api/families/${family.id}/status`, {
          isActive: true
        })

        if (response.data.success) {
          this.$message.success('家庭已启用')
          this.loadFamilies()
        } else {
          this.$message.error(response.data.message || '启用失败')
        }
      } catch (error) {
        console.error('启用家庭失败:', error)
        this.$message.error('启用失败')
      }
    },

    async deleteFamily(family) {
      try {
        const result = await this.$confirm(
          `确定要删除家庭"${family.name || family.familyName}"吗？此操作不可恢复！`,
          '确认删除',
          {
            confirmButtonText: '删除',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        const response = await this.$http.delete(`/admin/api/families/${family.id}`)

        if (response.data.success) {
          this.$message.success('家庭已删除')
          this.loadData() // 重新加载数据和统计
        } else {
          this.$message.error(response.data.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除家庭失败:', error)
          this.$message.error('删除失败')
        }
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN')
    }
  }
}
</script>

<style scoped>
.admin-family-management {
  padding: 20px;
}

/* 页面头部 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
}

.header-left h1 {
  margin: 0 0 8px 0;
  font-size: 28px;
  color: #333;
}

.header-left p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

/* 概览卡片 */
.overview-cards {
  margin-bottom: 30px;
}

.overview-card {
  background: #fff;
  border-radius: 12px;
  padding: 25px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.overview-card:hover {
  transform: translateY(-5px);
}

.card-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
}

.card-icon.families {
  background: linear-gradient(45deg, #667eea, #764ba2);
}

.card-icon.members {
  background: linear-gradient(45deg, #f093fb, #f5576c);
}

.card-icon.devices {
  background: linear-gradient(45deg, #4facfe, #00f2fe);
}

.card-icon.avg {
  background: linear-gradient(45deg, #43e97b, #38f9d7);
}

.card-icon i {
  font-size: 24px;
  color: #fff;
}

.card-number {
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin-bottom: 5px;
}

.card-label {
  color: #666;
  font-size: 14px;
}

/* 家庭列表区域 */
.family-list-section {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
  border-bottom: 1px solid #eee;
}

.section-header h2 {
  margin: 0;
  font-size: 20px;
  color: #333;
}

/* 家庭网格 */
.family-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
  padding: 30px;
}

.family-card {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.family-card:hover {
  border-color: #667eea;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.15);
  transform: translateY(-3px);
}

.family-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.family-avatar {
  width: 50px;
  height: 50px;
  background: linear-gradient(45deg, #667eea, #764ba2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.family-avatar i {
  font-size: 20px;
  color: #fff;
}

.family-info {
  flex: 1;
}

.family-name {
  margin: 0 0 5px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.family-id {
  margin: 0;
  font-size: 12px;
  color: #999;
  font-family: monospace;
}

.family-stats {
  display: flex;
  gap: 30px;
  margin-bottom: 20px;
  padding: 15px 0;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
}

.stat-item {
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 24px;
  font-weight: bold;
  color: #667eea;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 12px;
  color: #666;
}

.family-meta {
  margin-bottom: 20px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #666;
}

.meta-item:last-child {
  margin-bottom: 0;
}

.meta-item i {
  color: #999;
}

.family-actions {
  display: flex;
  gap: 10px;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #999;
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 20px;
  color: #ddd;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
}

/* 分页样式 */
.pagination-wrapper {
  padding: 20px 30px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: center;
  background: #fff;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .family-grid {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  }
}

@media (max-width: 768px) {
  .admin-family-management {
    padding: 15px;
  }
  
  .page-header {
    flex-direction: column;
    gap: 20px;
  }
  
  .section-header {
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
  }
  
  .family-grid {
    grid-template-columns: 1fr;
    gap: 15px;
    padding: 20px;
  }
  
  .family-card {
    padding: 20px;
  }
  
  .overview-cards .el-col {
    margin-bottom: 15px;
  }
  
  .pagination-wrapper {
    padding: 15px 20px;
  }
  
  .pagination-wrapper .el-pagination {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }
}
</style>